import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api/client';
import { useAuth } from './AuthContext';
import { playNotificationChime } from '../utils/notificationSound';
import './NotificationDropdown.css';

const NotificationContext = createContext(null);

const POLL_MS = 25000;

function NotificationDropdown({
  open,
  onClose,
  items,
  loadingList,
  unreadCount,
  markRead,
  markAllRead,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (e.target.closest('.notify-bell-btn')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="notify-panel glass-panel"
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="notify-panel-header">
        <span>Notifications</span>
        {unreadCount > 0 && (
          <button type="button" className="notify-mark-all" onClick={() => markAllRead()}>
            Mark all read
          </button>
        )}
      </div>
      <div className="notify-panel-body">
        {loadingList && <p className="notify-muted">Loading…</p>}
        {!loadingList && items.length === 0 && (
          <p className="notify-muted">You’re all caught up.</p>
        )}
        {!loadingList &&
          items.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`notify-item${n.read_at ? ' notify-item--read' : ''}`}
              onClick={() => {
                if (!n.read_at) markRead(n.id);
                if (n.action_url) {
                  window.location.href = n.action_url;
                }
              }}
            >
              <span className="notify-item-title">{n.title}</span>
              <span className="notify-item-body">{n.body}</span>
              <span className="notify-item-time">
                {n.created_at
                  ? new Date(n.created_at).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : ''}
              </span>
            </button>
          ))}
      </div>
    </div>,
    document.body,
  );
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const prevUnreadRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    const res = await api.get('/notifications/unread-count');
    const n = typeof res?.unread_count === 'number' ? res.unread_count : 0;
    if (prevUnreadRef.current !== null && n > prevUnreadRef.current) {
      playNotificationChime();
    }
    prevUnreadRef.current = n;
    setUnreadCount(n);
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get('/notifications?per_page=20');
      const rows = Array.isArray(res?.data) ? res.data : [];
      setItems(rows);
    } catch {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      prevUnreadRef.current = null;
      setUnreadCount(0);
      setItems([]);
      setOpen(false);
      return undefined;
    }
    prevUnreadRef.current = null;
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchUnread]);

  const markRead = useCallback(
    async (notificationId) => {
      await api.post(`/notifications/${notificationId}/read`, {});
      await fetchUnread();
      setItems((prev) =>
        prev.map((row) =>
          row.id === notificationId ? { ...row, read_at: new Date().toISOString() } : row,
        ),
      );
    },
    [fetchUnread],
  );

  const markAllRead = useCallback(async () => {
    await api.post('/notifications/read-all', {});
    await fetchUnread();
    setItems((prev) =>
      prev.map((row) => ({ ...row, read_at: row.read_at || new Date().toISOString() })),
    );
  }, [fetchUnread]);

  const toggleOpen = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const onBellClick = useCallback(() => {
    setOpen((o) => {
      const next = !o;
      if (next) {
        fetchList();
      }
      return next;
    });
  }, [fetchList]);

  const value = useMemo(
    () => ({
      unreadCount,
      items,
      open,
      setOpen,
      toggleOpen,
      onBellClick,
      loadingList,
      fetchUnread,
      fetchList,
      markRead,
      markAllRead,
    }),
    [
      unreadCount,
      items,
      open,
      toggleOpen,
      onBellClick,
      loadingList,
      fetchUnread,
      fetchList,
      markRead,
      markAllRead,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {user && (
        <NotificationDropdown
          open={open}
          onClose={close}
          items={items}
          loadingList={loadingList}
          unreadCount={unreadCount}
          markRead={markRead}
          markAllRead={markAllRead}
        />
      )}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
